import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Block, BreakpointOverrides } from "@alchimie/shared";
import { api } from "../api.js";
import { Button, COLORS, Empty } from "../ui.js";
import { BlocksWorkspace } from "./BlocksWorkspace.js";

interface SalesPage {
  id: string;
  espaceId: string;
  offerId: string;
  slug: string;
  mode: "TEMPLATE" | "BUILDER" | "CODE";
  blocks: Block[];
  breakpointOverrides: BreakpointOverrides;
  isPublished: boolean;
  offer?: { id: string; title: string; priceCents: number; currency: string };
  espace?: { slug: string; name: string };
}

/**
 * Builder de pages de vente — wrapper du BlocksWorkspace avec la topbar
 * spécifique (offer, slug, publish, delete, dirty indicator).
 */
export default function Builder() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const nav = useNavigate();

  const { data: sp, isLoading } = useQuery({
    queryKey: ["sales-page", id],
    queryFn: () => api.get<SalesPage>(`/sales-pages/${id}`),
    enabled: !!id,
  });

  const [blocks, setBlocks] = useState<Block[]>([]);
  const [overrides, setOverrides] = useState<BreakpointOverrides>({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (sp) {
      setBlocks(sp.blocks ?? []);
      setOverrides((sp.breakpointOverrides ?? {}) as BreakpointOverrides);
      setDirty(false);
    }
  }, [sp?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (next: { blocks?: Block[]; breakpointOverrides?: BreakpointOverrides; isPublished?: boolean }) =>
      api.patch<SalesPage>(`/sales-pages/${id}`, next),
    onSuccess: (data) => {
      qc.setQueryData(["sales-page", id], data);
      qc.invalidateQueries({ queryKey: ["sales-pages"] });
      setDirty(false);
    },
  });

  const del = useMutation({
    mutationFn: () => api.del(`/sales-pages/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sales-pages"] });
      nav("/sales-pages");
    },
  });

  if (isLoading) return <Empty>Chargement…</Empty>;
  if (!sp) return <Empty>Page introuvable.</Empty>;

  return (
    <BlocksWorkspace
      blocks={blocks}
      onBlocksChange={(b) => {
        setBlocks(b);
        setDirty(true);
      }}
      overrides={overrides}
      onOverridesChange={(o) => {
        setOverrides(o);
        setDirty(true);
      }}
      onPersist={() => save.mutate({ blocks, breakpointOverrides: overrides })}
      topbarLeft={
        <button
          onClick={() => nav("/sales-pages")}
          style={{
            border: `1px solid ${COLORS.border}`,
            background: "transparent",
            padding: "6px 12px",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: 13,
            fontFamily: "inherit",
          }}
        >
          ← Pages de vente
        </button>
      }
      topbarTitle={
        <>
          {sp.offer?.title}
          <span style={{ color: COLORS.muted, fontWeight: 400, marginLeft: 8 }}>/{sp.slug}</span>
        </>
      }
      topbarRight={
        <>
          <div style={{ fontSize: 12, color: COLORS.muted, marginLeft: 8 }}>
            {dirty ? "Modifications non enregistrées" : "À jour"}
          </div>
          <Button
            onClick={() => save.mutate({ blocks, breakpointOverrides: overrides })}
            disabled={!dirty || save.isPending}
          >
            {save.isPending ? "Enregistrement…" : "Enregistrer"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => save.mutate({ blocks, breakpointOverrides: overrides, isPublished: !sp.isPublished })}
          >
            {sp.isPublished ? "Dépublier" : "Publier"}
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              if (confirm("Supprimer cette page de vente ?")) del.mutate();
            }}
          >
            ✕
          </Button>
        </>
      }
    />
  );
}
